import { apiClient } from '@/api/api'
import type { AxiosResponse } from 'axios'
import type { UserParametersResponse, UpdateParameterPayload } from '@/types/parameters'
import type { MeasurementType } from '@/features/survey/types/survey-config'

/**
 * Преобразует measurementType из camelCase в snake_case для использования в URL
 * Примеры: 'footLength' -> 'foot_length', 'shoulderWidth' -> 'shoulder_width'
 */
function measurementTypeToSnakeCase(measurementType: MeasurementType): string {
  return measurementType.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/**
 * Получение всех параметров пользователя с метаданными
 * GET /user/parameters
 */
export const getUserParameters = async (): Promise<AxiosResponse<UserParametersResponse>> => {
  return apiClient.get<UserParametersResponse>('/user/parameters')
}

/**
 * Интерфейс ответа при обновлении параметра
 * PATCH /user/parameters/{measurement_type} возвращает этот формат
 */
export interface UpdateParameterResponse {
  success: boolean
  measurementType: MeasurementType
  value: number | null
  /** BE-145 — set when a `soft` write kept a value outside the usual range. */
  warning?: MeasurementRangeIssue | null
}

/** BE-145 — one row of the 422 `detail[]` / the soft write's `warning`; cm / kg as stored. */
export interface MeasurementRangeIssue {
  code: 'out_of_range' | 'out_of_hard_range' | 'out_of_usual_range'
  measurementType: string
  /** The USUAL range — also on `out_of_hard_range` (BE-145). */
  min: number
  max: number
  value: number
  /** Which range the value crossed (`null` from an API before the field). */
  bound?: 'usual' | 'hard' | null
  /** The hard bound the API refuses past even a soft write (`null` when not sent). */
  hardMin?: number | null
  hardMax?: number | null
}

/** The range a refusal should name: the hard bound when that was crossed, else the usual one. */
export function measurementCrossedRange(
  issue: MeasurementRangeIssue | null,
): { min: number; max: number } | null {
  if (!issue) return null
  const hard = issue.bound === 'hard' || (issue.bound == null && issue.code === 'out_of_hard_range')
  if (!hard) return { min: issue.min, max: issue.max }
  // An older API sent only the usual range on a hard refusal — nothing honest to name then.
  return issue.hardMin != null && issue.hardMax != null ? { min: issue.hardMin, max: issue.hardMax } : null
}

/** The range refusal of a 422 (`out_of_range` / `out_of_hard_range`), or null for any other error. */
export function measurementRangeIssue(error: unknown): MeasurementRangeIssue | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  if (response?.status !== 422 || !Array.isArray(response.data?.detail)) return null
  const row = (response.data.detail as Array<Record<string, unknown>>).find(
    (item) => item?.code === 'out_of_range' || item?.code === 'out_of_hard_range',
  )
  if (!row) return null
  const optionalNumber = (raw: unknown): number | null => (raw == null || raw === '' ? null : Number(raw))
  const bound = row.bound === 'hard' || row.bound === 'usual' ? row.bound : null
  return {
    code: row.code as MeasurementRangeIssue['code'],
    measurementType: String(row.measurementType ?? row.measurement_type ?? ''),
    min: Number(row.min),
    max: Number(row.max),
    value: Number(row.value),
    bound,
    hardMin: optionalNumber(row.hardMin ?? row.hard_min),
    hardMax: optionalNumber(row.hardMax ?? row.hard_max),
  }
}

/**
 * Обновление одного параметра
 * PATCH /user/parameters/{measurement_type}
 */
export const updateParameter = async (
  measurementType: MeasurementType,
  payload: UpdateParameterPayload
): Promise<AxiosResponse<UpdateParameterResponse>> => {
  const measurementTypeSnake = measurementTypeToSnakeCase(measurementType)
  return apiClient.patch<UpdateParameterResponse>(
    `/user/parameters/${measurementTypeSnake}`,
    payload
  )
}

/**
 * Сброс всех параметров пользователя
 * DELETE /user/parameters
 */
export const resetParameters = async (): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>('/user/parameters')
}

/**
 * BE-145 — `POST /user/parameters/restore`: the snapshot archived by the last DELETE comes back
 * (a value typed after the reset is overwritten); 404 `nothing_to_restore` when none was kept.
 */
export const restoreParameters = async (): Promise<AxiosResponse<{ parameters: unknown[] }>> => {
  return apiClient.post<{ parameters: unknown[] }>('/user/parameters/restore')
}

